import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {URL_ADMIN, URL_HQ, URL_HQ_JOIN_COUNSEL_ADD, URL_HQ_JOIN_COUNSEL_DETAIL} from 'routes/AdminRoute';
import {BaseSection} from 'components/layouts';
import {Button, Checkbox, Col, DatePicker, Divider, Flex, Input, Row, Select, Space} from 'antd';
import {AlertModal, BaseContainer, SearchDesc} from 'components/containers';
import {BaseButton, DataGrid, FillButton, ExcelButton, BaseDatePicker} from 'components/elements';
import {GridSetting} from 'pages/common';
import {getListAPI} from 'api/common';
import {fnSetColumn, getFormatDate, utilSearchData} from 'utils/utils';
import {
  joinCognitivePathOptions,
  counselListProgressStatusOptions,
  counselProgressStatusOptions,
  searchTypeOptions,
} from 'data/options';
import {getLocalStorage} from 'hooks/useLocalStorage';
import {exportStyleExcelDownload} from 'utils/file';
import {IconDownloadExcel} from 'components/assets/icons';
import dayjs from 'dayjs';

const JoinCounsel = () => {
  const navigate = useNavigate();
  const menu = getLocalStorage('openMenuInfo');
  const [excelHeaders, setExcelHeaders] = useState([])
  const [total, setTotal] = useState({})
  const [joinCounselList, setJoinCounselList] = useState([]);
  const [colList, setColList] = useState({
    gridSettingList: [],
    gridList: []
  });
  const [searchParams, setSearchParams] = useState({
    inquiry_start_date: '', // 검색시작날짜
    inquiry_end_date: '', // 검색완료날짜
    search_state: 'branch_name',
    search_content: '', // 감색조건입력
    progress_status: '-1' // 진행상태 default 는 -1 (전체)
  });
  const [alertModal, setAlertModal] = useState({
    open: false,
    message: ''
  });

  const fnCloseAlertModal = () => {
    setAlertModal({...alertModal, open: false, message: ''});
  };

  const fnExcelDownload = () => {
    const processData = joinCounselList?.map((item) => {
      let progress = '';
      if (item?.PROGRESS_STATUS === '0') {
        progress = '지사배정대기';
      } else if (item?.PROGRESS_STATUS === '1') {
        progress = '지사배정';
      } else if (item?.PROGRESS_STATUS === '2') {
        progress = '교육원배정';
      } else if (item?.PROGRESS_STATUS === '3') {
        progress = '상담완료';
      } else if (item?.PROGRESS_STATUS === '4') {
        progress = '입회대기';
      } else if (item?.PROGRESS_STATUS === '5') {
        progress = '입회승인';
      }

      const obj = {}
      colList?.gridList.forEach(it => {
        if ('PROGRESS_STATUS' === it.dataIndex) {
          obj['PROGRESS_STATUS'] = progress
        } else if ('COGNITIVE_PATH' === it.dataIndex) {
          obj['COGNITIVE_PATH'] = item?.COGNITIVE_PATH?.split(',')?.map(it => joinCognitivePathOptions?.find(item => item.value === it)?.label)?.join(',')
        } else {
          obj[it.dataIndex] = item[it.dataIndex]
        }
      });
      return obj
    });

    const headerList = colList?.gridList.map(it => it.title)
    exportStyleExcelDownload(headerList, processData, menu?.depth?.[1]);
  };

  // 입회상담리스트 가져오기
  const fnGetList = async () => {
    try {
      // 공통 서치바 데이터 체크 및 데이터 리턴
      const result = utilSearchData(searchParams);
      if (typeof result === 'string') {
        setAlertModal({...alertModal, open: true, message: result});
      } else {
        const {data} = await getListAPI(result);
        setJoinCounselList(data?.DATA_LIST?.map((it, idx) => ({no: data?.DATA_LIST?.length - idx, ...it})));
        setExcelHeaders(fnSetColumn(data.COLUMS_LIST, columns))
        setColList({
          ...colList,
          gridSettingList: data?.COLUMS_LIST,
          gridList: fnSetColumn(data?.COLUMS_LIST, columns)
        });
        setTotal({...total, no: data?.DATA_LIST?.length})
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fnGetList();
  }, []);

  const columns = [
    {
      title: '상담번호',
      align: 'center',
      dataIndex: 'IDX'
    },
    {
      title: '문의일',
      align: 'center',
      dataIndex: 'INQUIRY_DATE',
      render: (text) => {
        return getFormatDate(text);
      }
    },
    {
      title: '보호자명',
      align: 'center',
      dataIndex: 'PARENT_NAME'
    },
    {
      title: '자녀명',
      align: 'center',
      dataIndex: 'MEMBER_NAME'
    },
    {
      title: '자녀나이',
      align: 'center',
      dataIndex: 'AGE',
      render: (text) => {
        return <span>{text}세</span>
      }
    },
    {
      title: '휴대폰번호',
      align: 'center',
      dataIndex: 'CELL_PHONE'
    },
    {
      title: '주소',
      align: 'center',
      dataIndex: 'HOPE_AREA'
    },
    {
      title: '상세주소',
      align: 'center',
      dataIndex: 'ADDR_DETAIL'
    },
    {
      title: '인지경로',
      align: 'center',
      dataIndex: 'COGNITIVE_PATH',
      render: (text) => text?.split(',')?.map(it => joinCognitivePathOptions?.find(item => item.value === it)?.label)?.join(',')
    },
    {
      title: '지사명',
      align: 'center',
      dataIndex: 'BRANCH_NAME'
    },
    {
      title: '교육원명',
      align: 'center',
      dataIndex: 'SCHOOL_NAME'
    },
    {
      title: '진행상태',
      align: 'center',
      dataIndex: 'PROGRESS_STATUS',
      render: (value, record) => counselListProgressStatusOptions?.find(it => it.value === value)?.label
    },
    {
      title: 'SMS 발송',
      align: 'center',
      dataIndex: 'SMS_SEND',
      render: (text) => text === 1 && '발송완료'
    },
    {
      title: '작성자',
      align: 'center',
      dataIndex: 'WRITER'
    }
  ];

  // 검색조건
  const searchItem = [
    {
      key: 1,
      label: '문의일',
      children: 
        <Space>
          <BaseDatePicker
            value={searchParams?.inquiry_start_date}
            onChange={dateString => setSearchParams({ ...searchParams, inquiry_start_date: dateString })}
          />
          ~
          <BaseDatePicker
            value={searchParams?.inquiry_end_date}
            onChange={dateString => setSearchParams({ ...searchParams, inquiry_end_date: dateString})}
          />
        </Space>
    },
    {
      key: 2,
      label: '검색조건',
      children: (
        <Space>
          <Select style={{width: '12rem'}} value={searchParams?.search_state}
                  options={searchTypeOptions(['보호자명', '휴대폰번호', '지사명', '교육원명'])}
                  onChange={(value) => setSearchParams({...searchParams, search_state: value})}/>
          <Input value={searchParams?.search_content}
                 onChange={(value) => setSearchParams({...searchParams, search_content: value?.target?.value})} onPressEnter={fnGetList}/>
        </Space>
      )
    },
    {
      key: 3,
      label: '진행상태',
      span: 2,
      children: <Select style={{width: '12rem'}} value={searchParams?.progress_status}
                        options={counselProgressStatusOptions}
                        onSelect={(value) => setSearchParams({...searchParams, progress_status: value})}/>
    },
    {
      key: 4,
      label: '인지경로',
      children: <Checkbox.Group options={joinCognitivePathOptions} value={searchParams?.cognitive_path}
                                onChange={(value) => setSearchParams({...searchParams, cognitive_path: value})}/>
    }
  ];

  return (
    <BaseSection>
      <Flex gap={'7px'}>
        <Col flex="auto">
          <SearchDesc items={searchItem} column={2} onSearch={fnGetList}/>
        </Col>
      </Flex>
      <BaseContainer>
        <Row>
          <Col span={12}>
            <Space>
              <GridSetting colList={colList?.gridSettingList} fnUpdateCol={fnGetList}/>
              <ExcelButton
                // label={'엑셀 다운로드(검색)'} //뷰네임 미입력시 기본값
                // isCheckComp={true} //체크박스 컴포넌트 여부 미입력시 X
                headerList={excelHeaders} //필수 헤더리스트 표 설정과 동일한 변수여야함
                gridList={joinCounselList} //필수 실시간 그리드 데이터
                // filterList={[]} //뺄 칼럼 목록 dataIndex 미입력시 dataIndex 키 없는것만 뺌
                // renderList={[]} //코드 or 다른이름으로 바꿔서 다운할 목록
                // checkRows={} //isCheckComp true 일시 checkRows 데이터 미입력시 체크작동안함
                // checkKey={} //isCheckComp true 일시 check 한 데이터 dataIndex 키 네임 미입력시 체크작동안함
                // fileName={} //파일네임 미입력시 해당 메뉴이름
              />
            </Space>
          </Col>
          <Col span={12}>
            <Flex justify="flex-end">
              <FillButton label="등록" backgroundColor="var(--color-gray03)"
                          onClick={() => navigate(`${URL_ADMIN + URL_HQ}${URL_HQ_JOIN_COUNSEL_ADD}`)}/>
            </Flex>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Divider className="divider-small"/>
            <DataGrid
              rowKey={'IDX'}
              columns={excelHeaders}
              data={joinCounselList}
              pagination
              onRow={(record, rowIndex) => {
                return {
                  className: 'text-click',
                  onClick: (event) => {
                    navigate(`${URL_ADMIN + URL_HQ}${URL_HQ_JOIN_COUNSEL_DETAIL.replace(':no', record.IDX)}`);
                  }, // click row
                };
              }}
            />
          </Col>
        </Row>
      </BaseContainer>
      {/* alert 모달 */}
      <AlertModal open={alertModal?.open} onCancel={fnCloseAlertModal}>
        {alertModal?.message}
      </AlertModal>
    </BaseSection>
  );
}

export default JoinCounsel;
